import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Badge } from '@/backoffice/components/ui';
import {
  fetchDriverInsurance,
  type DriverInsuranceData,
} from '@/domains/driver/services/driver.service';
import {
  Shield,
  Check,
  AlertTriangle,
  FileText,
  Phone,
  MessageSquare,
  Car,
  Package,
  Loader2,
} from 'lucide-react';

export function ProInsurancePage() {
  const [insurance, setInsurance] = useState<DriverInsuranceData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDriverInsurance()
      .then(setInsurance)
      .catch(() => setInsurance(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <DashboardLayout userType="pro_driver" title="Assurance">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const coverage = insurance?.coverage;
  const claims = insurance?.claims ?? [];

  return (
    <DashboardLayout userType="pro_driver" title="Assurance">
      <Card className={`mb-6 ${insurance?.is_active ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
        <div className="flex items-start gap-4">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
            insurance?.is_active ? 'bg-emerald-100' : 'bg-red-100'
          }`}>
            <Shield className={`w-8 h-8 ${insurance?.is_active ? 'text-emerald-600' : 'text-red-600'}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-gray-900">
                {insurance?.is_active ? 'Assurance active' : 'Aucune assurance active'}
              </h3>
              {insurance?.is_active && insurance.expiry_date && (
                <Badge variant="success">Valide jusqu'au {insurance.expiry_date}</Badge>
              )}
            </div>
            <p className="text-sm text-gray-600">
              {insurance?.is_active
                ? 'Votre assurance couvre votre véhicule, les marchandises transportées et vous protège en cas d\'accident.'
                : 'Veuillez déposer et valider votre document d\'assurance dans la section Documents.'}
            </p>
          </div>
        </div>
      </Card>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {[
          { key: 'vehicle', label: 'Véhicule', desc: 'Protection en cas de panne, d\'accident ou de vol.' },
          { key: 'goods', label: 'Marchandises', desc: `Couverture jusqu'à ${(coverage?.max_coverage ?? 0).toLocaleString()} GNF pour les colis.` },
          { key: 'accidents', label: 'Accidents', desc: 'Protection personnelle en cas d\'accident pendant le travail.' },
        ].map((item) => {
          const covered = coverage?.[item.key as keyof typeof coverage] === true;
          return (
            <Card key={item.key}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${covered ? 'bg-emerald-100' : 'bg-gray-100'}`}>
                  {covered ? <Check className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-gray-400" />}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{item.label}</p>
                  <Badge variant={covered ? 'success' : 'default'} size="sm">
                    {covered ? 'Couvert' : 'Non couvert'}
                  </Badge>
                </div>
              </div>
              <p className="text-sm text-gray-500">{item.desc}</p>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Sinistres récents</h3>
          </div>

          {claims.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Aucun sinistre déclaré</p>
            </div>
          ) : (
            <div className="space-y-3">
              {claims.map((claim) => (
                <div key={claim.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{claim.type}</p>
                    <p className="text-sm text-gray-500">{claim.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">{claim.amount.toLocaleString()} GNF</p>
                    <Badge variant={claim.status === 'resolved' ? 'success' : 'warning'} size="sm">
                      {claim.status === 'resolved' ? 'Résolu' : 'En cours'}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card>
          <h3 className="font-semibold text-gray-900 mb-4">En cas d'urgence</h3>
          <div className="p-4 bg-red-50 rounded-lg border border-red-200 mb-4">
            <p className="text-sm text-red-800 mb-2">
              En cas d'accident ou d'incident pendant une livraison, contactez immédiatement notre service d'assistance.
            </p>
            <p className="text-lg font-bold text-red-900">{insurance?.emergency.phone ?? '+224 622 24 24 24'}</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Car className="w-5 h-5 text-gray-500 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Accident de la route</p>
                <p className="text-sm text-gray-600">Appelez le 17 ou notre ligne d'urgence</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <Package className="w-5 h-5 text-gray-500 mt-0.5" />
              <div>
                <p className="font-medium text-gray-900">Colis endommagé</p>
                <p className="text-sm text-gray-600">Prenez des photos et déclarez le sinistre</p>
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <a href={`tel:${insurance?.emergency.phone}`} className="flex-1">
              <Button variant="outline" className="w-full" leftIcon={<Phone className="w-4 h-4" />}>
                Appeler
              </Button>
            </a>
            <Button variant="primary" className="flex-1" leftIcon={<MessageSquare className="w-4 h-4" />}>
              Chat assistance
            </Button>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
